<script lang="ts">
  import type { Snippet } from 'svelte';
  import { twMerge } from 'tailwind-merge';
  import ConfirmationBadge from './ConfirmationBadge.svelte';
  import Icon from './Icon.svelte';
  import Spinner from './Spinner.svelte';

  type Variant =
    | 'primary'
    | 'secondary'
    | 'link'
    | 'destructive'
    | 'delete'
    | 'confirm'
    | 'warning';
  type Size = 'sm' | 'md' | 'lg';

  interface Props {
    variant?: Variant;
    size?: Size;
    disabled?: boolean;
    href?: string | null;
    target?: string | null;
    fullWidth?: boolean;
    loading?: boolean;
    iconLeft?: string | null;
    expandDown?: boolean;
    iconRight?: string | null;
    type?: 'button' | 'submit' | 'reset';
    autofocus?: boolean;
    wrap?: boolean;
    dataTestId?: string | undefined;
    class?: string | null;
    /**
     * Fired on click. Not called for `variant="destructive"` — that opens the
     * confirmation badge instead and the action fires via `onconfirm`.
     */
    onclick?: (event: MouseEvent) => void;
    /** Fired when a destructive button's confirmation is accepted. */
    onconfirm?: () => void;
    children?: Snippet;
  }

  let {
    variant = 'primary',
    size = 'md',
    disabled = false,
    href = null,
    target = null,
    fullWidth = false,
    loading = false,
    iconLeft = null,
    expandDown = false,
    iconRight = null,
    type = 'button',
    autofocus = false,
    wrap = false,
    dataTestId = undefined,
    class: className = null,
    onclick,
    onconfirm,
    children,
  }: Props = $props();

  let confirmExpanded = $state(false);

  // $derived, not a plain const: the hover classes below are gated on
  // `disabled`, so a const would bake in whatever `disabled` was at init and
  // leave hover styling stale when it toggles at runtime.
  const variantMap: Record<Variant, string> = $derived({
    primary: twMerge([
      'border border-white bg-white text-black',
      !disabled && 'hover:bg-gray-2 hover:border-gray-2',
    ]),
    secondary: twMerge([
      'border border-gray-6 bg-transparent text-white',
      !disabled && ' hover:bg-gray-8  ',
    ]),
    link: twMerge([
      'justify-start p-0 text-gray-4',
      !disabled && 'hover:text-white',
    ]),
    destructive: twMerge([
      'text-white bg-red border border-red',
      !disabled && 'hover:bg-red-700 hover:border-red-700',
    ]),
    delete: twMerge([
      'text-white bg-red border border-red',
      !disabled && 'hover:bg-red-700 hover:border-red-700',
    ]),
    warning: twMerge([
      'text-black bg-yellow border border-yellow hover:bg-opacity-75 hover:border-transparent',
    ]),
    confirm: twMerge([
      'text-white bg-[#4f7de0] border border-[#4f7de0] hover:bg-opacity-75 hover:border-transparent',
    ]),
  });
  const variants = $derived(variantMap[variant]);

  const sizeMap: Record<Size, string> = {
    sm: 'h-6 px-2 text-xs',
    md: 'h-8 px-4 text-md',
    lg: 'h-10 px-4 text-md',
  };
  const sizes = $derived(sizeMap[size]);

  const classes = $derived(
    twMerge([
      'flex justify-center leading-4 rounded-sm items-center font-mono transition duration-150 ease-in',
      fullWidth && 'w-full',
      sizes,
      variants,
      disabled && ['opacity-50 cursor-not-allowed'],
      className,
    ]),
  );
  const hrefClass = $derived(
    twMerge([
      'flex justify-center items-center gap-1',
      !wrap && 'flex-shrink-0 whitespace-nowrap',
    ]),
  );

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (variant === 'destructive') {
      confirmExpanded = true;
    } else {
      onclick?.(event);
    }
  };

  const handleConfirm = () => {
    confirmExpanded = false;
    onconfirm?.();
  };
</script>

{#if href}
  <a {onclick} {href} {target} class={classes}>
    <div class={hrefClass}>
      {#if loading}
        <Spinner size="lg" />
      {:else}
        {#if iconLeft}
          <Icon icon={iconLeft} {size} />
        {/if}
        {@render children?.()}
        {#if iconRight}
          <Icon icon={iconRight} {size} />
        {/if}
      {/if}
    </div>
  </a>
{:else}
  <div class="relative">
    {#if variant === 'destructive' && confirmExpanded}
      <div
        class="z-1 absolute -top-0.5 left-1/2 -translate-x-1/2 {expandDown
          ? 'translate-y-full'
          : '-translate-y-full'}"
      >
        <ConfirmationBadge
          onconfirm={handleConfirm}
          {expandDown}
          oncancel={() => (confirmExpanded = false)}
        />
      </div>
    {/if}
    <!-- svelte-ignore a11y_autofocus -->
    <button
      {type}
      onclick={handleClick}
      disabled={disabled || loading}
      class={classes}
      data-testid={dataTestId}
      {autofocus}
    >
      <div
        class="flex flex-shrink-0 items-center justify-center gap-1 whitespace-nowrap"
      >
        {#if iconLeft}
          {#if loading}
            <Spinner {size} />
          {:else}
            <Icon icon={iconLeft} {size} />
          {/if}
        {/if}
        <div
          class="relative {loading && !iconLeft && !iconRight && 'opacity-0'}"
        >
          {@render children?.()}
        </div>
        {#if loading && !iconLeft && !iconRight}
          <div class="absolute flex w-full justify-center">
            <Spinner size="lg" />
          </div>
        {/if}
        {#if iconRight}
          {#if loading}
            <Spinner {size} />
          {:else}
            <Icon icon={iconRight} {size} />
          {/if}
        {/if}
      </div>
    </button>
  </div>
{/if}
